import { computed, observable } from 'mobx';
import type { FlightSliceResponseArgs } from './flight_slice2';
import FlightSlice, { convertFlightSliceResponseToArgs } from './flight_slice2';
import type ReservingTripItem from '../trip/reserving_trip_item2';
import type MarginType from '../organization/margin_type3';

export type FlightResponseArgs = {
  id: string;
  price: number;
  itineraries: FlightSliceResponseArgs[];
  description: string;
  fee: number;
  seat_reservation_enable: boolean;
  outword?: FlightSliceResponseArgs;
  homeword?: FlightSliceResponseArgs;
};

export type FlightArgs = {
  id?: string;
  price?: number;
  itineraries?: FlightSlice[];
  description?: string;
  fee?: number;
  seatReservationEnable?: boolean;
  outword?: FlightSlice;
  homeword?: FlightSlice;
};

export const convertFlightResponseToArgs = (response: FlightResponseArgs) => {
  const args: FlightArgs = {
    id: response.id,
    price: response.price,
    itineraries: response.itineraries.map(it => new FlightSlice(convertFlightSliceResponseToArgs(it))),
    description: response.description,
    fee: response.fee,
    seatReservationEnable: response.seat_reservation_enable,
    outword: response.outword ? new FlightSlice(convertFlightSliceResponseToArgs(response.outword)) : undefined,
    homeword: response.homeword ? new FlightSlice(convertFlightSliceResponseToArgs(response.homeword)) : undefined
  };
  return args;
};

export default class Flight {
  @observable id?: string;
  @observable price: number;
  @observable itineraries: FlightSlice[];
  @observable description?: string;
  @observable parentItem?: ReservingTripItem;
  @observable fee: number;
  @observable seatReservationEnable: boolean;
  @observable outword?: FlightSlice;
  @observable homeword?: FlightSlice;

  constructor(args: FlightArgs = {}) {
    this.id = args.id;
    this.price = args.price ?? 0;
    this.itineraries = args.itineraries ?? [];
    this.description = args.description ?? '';
    this.fee = args.fee ?? 0;
    this.seatReservationEnable = args.seatReservationEnable ?? false;
    this.outword = args.outword;
    this.homeword = args.homeword;
  }

  @computed get totalPrice(): number {
    return this.price;
  }

  @computed get startDate(): string | undefined {
    return this.itineraries[0]?.startDate;
  }

  @computed get lastItinerary(): FlightSlice | undefined {
    return this.itineraries[this.itineraries.length - 1];
  }

  @computed get endDate(): string | undefined {
    return this.lastItinerary?.endDate;
  }

  @computed get peopleNum(): number {
    return this.parentItem?.peopleNum ?? 1;
  }

  @computed get showFee(): boolean {
    return this.parentItem?.showFee ?? true;
  }

  @computed get marginType(): MarginType | undefined {
    return this.parentItem?.marginType;
  }

  @computed get marginAmount(): number {
    if (this.marginType) {
      return this.marginType.calcMarginAmount(this.price, this.peopleNum, 2);
    }
    return this.fee * this.peopleNum;
  }

  @computed get feeText(): string {
    if (this.marginType) {
      return this.marginType.describe(this.price, this.peopleNum, 2);
    }
    return `${utils.formatPrice(this.fee)} × ${this.peopleNum}名`;
  }

  @computed get priceDetailArray(): { title: string; price: string }[] {
    const arr = [];
    arr.push({ title: 'チケット代金:', price: `${utils.formatPrice(this.price)}(${this.peopleNum}名分)` });
    if (this.showFee && this.marginAmount > 0) {
      arr.push({ title: '発券手数料:', price: this.feeText });
    }
    return arr;
  }

  @computed get priceDetailText(): string {
    let text = `└ チケット代金: ${utils.formatPrice(this.price)}(${this.peopleNum}名分)`;
    if (this.showFee && this.marginAmount > 0) {
      text += `\n└ 発券手数料: ${this.feeText}`;
    }
    return text;
  }

  @computed get priceText(): string {
    return `航空券：${utils.formatPrice(this.totalPrice)}\n${this.priceDetailText}`;
  }

  @computed get totalBufferAmount(): number {
    if (!this.parentItem?.organization) {
      return 0;
    }
    return this.parentItem.organization.getForeignFlightBufferAmount() * this.peopleNum;
  }

  @computed get bufferAmountText(): string {
    return `${utils.digits(this.parentItem?.organization?.getForeignFlightBufferAmount() ?? 0)}円 × ${
      this.peopleNum
    }名`;
  }

  sliceFromSubtype(subtype: 'outword' | 'homeword' | number) {
    if (subtype === 'outword') {
      return this.outword;
    }
    if (subtype === 'homeword') {
      return this.homeword;
    }
    return this.itineraries[subtype];
  }
}
